import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import dateService from '@gem/services/date-service';

import { mapUserProfileBadgeToIcon } from 'APP/constants/userBadges';
import { FinishReasons } from 'APP/model/mainCall/mainCallModel.types';
import Entities from 'STORE';

export default (message) => {
  const {
    finishTs,
    startTs,
    startInitiatorId,
    initiatorId,
    duration,
    isFinishCall,
    finishReason,
    initiatorNickName,
  } = message.payload;

  const group = message.group;
  const { t } = useTranslation();
  const presenter = useLocalObservable(() => ({
    _finishTs: null,
    _duration: null,
    _finishReason: null,
    _isP2PGroup: false,

    setDuration(duration) {
      presenter._duration = duration;
    },

    setFinishTs(finishTs) {
      presenter._finishTs = finishTs;
    },

    setFinishReason(finishReason) {
      presenter._finishReason = finishReason;
    },

    setIsP2PGroup(isP2P) {
      presenter._isP2PGroup = isP2P;
    },

    get isLeftAligned() {
      return Entities.ChatStore.isOnlyLeftSideMessages;
    },

    get time() {
      return dateService.format(Number(presenter._finishTs || startTs), {
        hour: 'numeric',
        minute: 'numeric',
      });
    },

    get isIncoming() {
      return Entities.usersStore.Me.id !== startInitiatorId;
    },

    get isMeInitiator() {
      return Entities.usersStore.Me.id === initiatorId;
    },

    get duration() {
      if (typeof presenter._duration === 'number' && presenter._duration >= 0) {
        return dateService.getDurationText(presenter._duration);
      }
      return '';
    },

    get reason() {
      if (
        isFinishCall &&
        [FinishReasons.Declined, FinishReasons.Unanswered].includes(presenter._finishReason)
      ) {
        return presenter.isIncoming ? 'missed' : 'cancel';
      }
      return null;
    },

    get isShowInitiator() {
      if (
        presenter._isP2PGroup &&
        isFinishCall &&
        presenter._finishReason === FinishReasons.Finished
      ) {
        return Boolean(initiatorId);
      }
      if (!presenter._isP2PGroup) {
        if (isFinishCall) {
          return Boolean(initiatorId);
        }
        return true;
      }
      return false;
    },

    get initiatorName() {
      if (presenter.isMeInitiator) {
        return t('chat_details_myself');
      }
      return Entities.usersStore.getUserById(initiatorId)?.displayName || initiatorNickName;
    },

    get userNameBadge() {
      if (presenter.isMeInitiator) {
        return null;
      }
      const user = Entities.usersStore.getUserById(initiatorId);

      return user?.usernameBadge ? mapUserProfileBadgeToIcon[user.usernameBadge] : null;
    },

    get verified() {
      if (presenter.isMeInitiator) {
        return false;
      }

      const user = Entities.usersStore.getUserById(initiatorId);

      return user?.verified;
    },

    get string() {
      if (presenter._isP2PGroup) {
        if (isFinishCall) {
          switch (presenter._finishReason) {
            case FinishReasons.Declined: {
              return t('calls_declined_call');
            }
            case FinishReasons.Unanswered: {
              return presenter.isIncoming ? t('calls_missed_call') : t('calls_no_answer');
            }
            default: {
              if (initiatorId) {
                return t('p2p_call_finished');
              }
              return t('group_call_finished');
            }
          }
        }
        return presenter.isIncoming ? t('call_incoming_call') : t('call_outgoing_call');
      }

      if (presenter.isMeInitiator) {
        if (isFinishCall) {
          return t('group_call_finished_for_initiator');
        }
        return t('call_you_started_a_call');
      }

      if (isFinishCall) {
        if (initiatorId) {
          return t('group_call_finished_for_receiver');
        }
        return t('group_call_finished');
      }

      return t('group_call_started_for_receiver');
    },

    get text() {
      const chunks = presenter.string.split('{0}');
      return {
        before: chunks[0],
        after: chunks[1] || '',
      };
    },
  }));

  useEffect(() => {
    presenter.setIsP2PGroup(group?.isP2P || false);
  }, [group?.isP2P]);

  useEffect(() => {
    presenter.setDuration(duration);
  }, [duration]);

  useEffect(() => {
    presenter.setFinishTs(finishTs);
  }, [finishTs]);

  useEffect(() => {
    presenter.setFinishReason(finishReason);
  }, [finishReason]);

  return presenter;
};
